import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { CarouselTab } from './LandingCarousel.types';

import { LandingCarouselCard } from './landing-carousel-card/LandingCarouselCard';
import { LandingCarouselContent } from './landing-carousel-content/LandingCarouselContent';
import styles from './LandingCarousel.module.css';
import {
  AUTO_SWITCH_INTERVAL_MS,
  CAROUSEL_TABS,
  getAdjacentTab,
  getNextTab,
} from './LandingCarousel.utils';

const LandingCarousel = React.memo((): React.ReactElement => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = React.useState<CarouselTab>('manage');
  const [isAutoPlaying, setIsAutoPlaying] = React.useState<boolean>(true);
  const [progress, setProgress] = React.useState<number>(0);
  const [isMobile, setIsMobile] = React.useState<boolean>(
    window.innerWidth <= 767
  );
  const tabsRef = React.useRef<HTMLDivElement>(null);
  const touchStartXRef = React.useRef<number | null>(null);
  const progressIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const switchTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect((): (() => void) => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth <= 767);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const clearTimers = React.useCallback((): void => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (switchTimeoutRef.current) {
      clearTimeout(switchTimeoutRef.current);
      switchTimeoutRef.current = null;
    }
  }, []);

  const startAutoPlay = React.useCallback((): void => {
    clearTimers();
    setProgress(0);

    const tickMs = 50;
    const totalTicks = AUTO_SWITCH_INTERVAL_MS / tickMs;

    progressIntervalRef.current = setInterval((): void => {
      setProgress(prev => {
        const next = prev + 100 / totalTicks;
        return next >= 100 ? 100 : next;
      });
    }, tickMs);

    switchTimeoutRef.current = setTimeout((): void => {
      setActiveTab(prev => getNextTab(prev));
      setProgress(0);
    }, AUTO_SWITCH_INTERVAL_MS);
  }, [clearTimers]);

  const centerActiveTab = React.useCallback((tabId: CarouselTab): void => {
    if (tabsRef.current) {
      const isTablet = window.innerWidth <= 767;

      if (!isTablet) {
        tabsRef.current.style.transform = '';
        return;
      }

      const activeCard = tabsRef.current.querySelector(
        `[data-tab-id="${tabId}"]`
      ) as HTMLElement | null;

      if (activeCard) {
        const containerWidth = tabsRef.current.parentElement?.offsetWidth ?? 0;
        const cardLeft = activeCard.offsetLeft;
        const cardWidth = activeCard.offsetWidth;
        const offset = containerWidth / 2 - cardLeft - cardWidth / 2;
        tabsRef.current.style.transform = `translateX(${offset}px)`;
      }
    }
  }, []);

  React.useEffect(() => {
    centerActiveTab(activeTab);
  }, [activeTab, centerActiveTab]);

  React.useEffect((): (() => void) => {
    if (isAutoPlaying) {
      startAutoPlay();
    }
    return clearTimers;
  }, [activeTab, isAutoPlaying, startAutoPlay, clearTimers]);

  React.useEffect(() => {
    centerActiveTab(activeTab);
  }, [activeTab, centerActiveTab]);

  const handleTabClick = React.useCallback((tabId: CarouselTab): void => {
    setActiveTab(tabId);
    setIsAutoPlaying(false);
    setProgress(0);
  }, []);

  const handleSwipe = React.useCallback((direction: 'next' | 'prev'): void => {
    setActiveTab(current => {
      const adjacent = getAdjacentTab(current, direction);
      return adjacent ?? current;
    });
    setIsAutoPlaying(false);
    setProgress(0);
  }, []);

  const handleTouchStart = React.useCallback(
    (e: React.TouchEvent<HTMLDivElement>): void => {
      touchStartXRef.current = e.touches[0].clientX;
    },
    []
  );

  const handleTouchEnd = React.useCallback(
    (e: React.TouchEvent<HTMLDivElement>): void => {
      if (touchStartXRef.current === null) return;

      const delta = e.changedTouches[0].clientX - touchStartXRef.current;
      touchStartXRef.current = null;

      if (Math.abs(delta) < 50) return;

      handleSwipe(delta < 0 ? 'next' : 'prev');
    },
    [handleSwipe]
  );

  const swipeHandlers = isMobile
    ? { onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd }
    : {};

  return (
    <section className={styles.landingCarousel} data-testid="landing-carousel">
      <h2 className={styles.landingCarouselTitle}>
        {t('landingPage.carousel.title')}
      </h2>

      <div className={styles.landingCarouselTabsWrapper} {...swipeHandlers}>
        <div
          ref={tabsRef}
          className={styles.landingCarouselTabs}
          role="tablist"
          aria-label={t('landingPage.carousel.tablistLabel')}
        >
          {CAROUSEL_TABS.map(tab => (
            <div key={tab.id} data-tab-id={tab.id}>
              <LandingCarouselCard
                tab={tab}
                isActive={activeTab === tab.id}
                isAutoPlaying={isAutoPlaying}
                progress={activeTab === tab.id ? progress : 0}
                onClick={handleTabClick}
              />
            </div>
          ))}
        </div>
      </div>

      <LandingCarouselContent activeTab={activeTab} />
    </section>
  );
});

LandingCarousel.displayName = 'LandingCarousel';

export { LandingCarousel };
